// src/components/messaging/ConversationThread.tsx
import React, { useEffect, useRef, useMemo, useState } from "react";
import { Avatar, Typography, Space, Button, Tooltip, Empty, Spin, Popover, List, Image, Modal, Alert } from "antd";
import { UserOutlined, ArrowLeftOutlined, CheckOutlined, DoubleRightOutlined, UserAddOutlined, TeamOutlined, DeleteOutlined, DownloadOutlined, FileOutlined, EyeOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Message, Conversation } from "../../types/messaging";
import { getConversationTitle, groupMessagesByDate, isOwnMessage, formatFileSize, getFileIcon, downloadAttachment } from "../../api/messaging";
import MessageInput from "./MessageInput";
import "./MessagingPanel.css";
import type { UserUuid } from "../../types/identity";

const { Text } = Typography;

interface ConversationThreadProps {
	conversation: Conversation;
	messages: Message[];
	typingUsers: UserUuid[];
	currentUserId: UserUuid;
	onSendMessage: (content: string, files?: File[]) => Promise<void>;
	onBack?: () => void;
	isLoading?: boolean;
	isPatient?: boolean;
	onAddParticipant?: () => void;
	onRemoveParticipant?: (userId: UserUuid) => Promise<void>;
	onCloseConversation?: () => Promise<void>;
}

const ConversationThread: React.FC<ConversationThreadProps> = ({ conversation, messages, typingUsers, currentUserId, onSendMessage, onBack, isLoading = false, isPatient = false, onAddParticipant, onRemoveParticipant, onCloseConversation }) => {
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const [participantsOpen, setParticipantsOpen] = useState(false);
	const [removingUserId, setRemovingUserId] = useState<UserUuid | null>(null);
	const [previewImage, setPreviewImage] = useState<string | null>(null);
	const [isClosing, setIsClosing] = useState(false);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);
	const conversationTitle = getConversationTitle(conversation, currentUserId);

	const activeParticipants = useMemo(() => (conversation.participants || []).filter((p) => p.is_active !== false), [conversation.participants]);

	const currentUserParticipant = useMemo(() => activeParticipants.find((p) => p.user_id === currentUserId), [activeParticipants, currentUserId]);

	const canCloseConversation = useMemo(() => !isPatient && currentUserParticipant?.role === "practitioner" && !conversation.is_closed && onCloseConversation, [isPatient, currentUserParticipant, conversation.is_closed, onCloseConversation]);

	const handleRemove = async (userId: UserUuid) => {
		if (!onRemoveParticipant) return;
		setRemovingUserId(userId);
		try {
			await onRemoveParticipant(userId);
		} finally {
			setRemovingUserId(null);
		}
	};

	const handleCloseConversation = () => {
		Modal.confirm({
			title: "Close Conversation",
			icon: <ExclamationCircleOutlined />,
			content: "Are you sure you want to close this conversation? Once closed, no one will be able to send new messages. The patient will need to submit a new conversation request.",
			okText: "Close Conversation",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				if (!onCloseConversation) return;
				setIsClosing(true);
				try {
					await onCloseConversation();
				} finally {
					setIsClosing(false);
				}
			},
		});
	};

	const handleAttachmentClick = (attachment: any) => {
		if (attachment.type.startsWith("image/")) {
			setPreviewImage(attachment.url);
		} else {
			downloadAttachment(attachment.url);
		}
	};

	const participantsPopoverContent = (
		<List
			size="small"
			dataSource={activeParticipants}
			locale={{ emptyText: "No participants" }}
			style={{ minWidth: 220 }}
			renderItem={(p) => {
				const name = p.user ? `${p.user.first_name} ${p.user.last_name}` : `User ${p.user_id}`;
				const isCurrentUser = p.user_id === currentUserId;
				const canRemove = !isPatient && !isCurrentUser && onRemoveParticipant && !conversation.is_closed;
				return (
					<List.Item>
						<List.Item.Meta
							avatar={<Avatar size={28} icon={!p.user?.profile_picture ? <UserOutlined /> : undefined} src={p.user?.profile_picture} />}
							title={
								<Text style={{ fontSize: 13 }}>
									{name}
									{isCurrentUser ? " (you)" : ""}
								</Text>
							}
							description={
								<Text type="secondary" style={{ fontSize: 11 }}>
									{p.role}
								</Text>
							}
						/>
						{canRemove && (
							<Tooltip title="Remove from conversation">
								<Button type="text" danger size="small" icon={<DeleteOutlined />} loading={removingUserId === p.user_id} onClick={() => handleRemove(p.user_id)} />
							</Tooltip>
						)}
					</List.Item>
				);
			}}
		/>
	);

	const typingUserNames = useMemo(() => {
		const othersTyping = typingUsers.filter((id) => id !== currentUserId);
		if (othersTyping.length === 0) return null;

		const names = othersTyping
			.map((typingUserId) => {
				const participant = conversation.participants?.find((p) => p.user_id === typingUserId);
				return participant?.user?.first_name || "Someone";
			})
			.slice(0, 3);

		if (names.length === 1) return `${names[0]} is typing...`;
		if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
		return `${names[0]}, ${names[1]} and ${names.length - 2} others are typing...`;
	}, [typingUsers, conversation.participants, currentUserId]);

	const renderAttachment = (attachment: any) => {
		const isImage = attachment.type.startsWith("image/");
		const icon = getFileIcon(attachment.type);

		if (isImage) {
			return (
				<div key={attachment.id} className="message-attachment message-attachment-image" onClick={() => handleAttachmentClick(attachment)}>
					<img src={attachment.url} alt={attachment.name} className="message-attachment-image-preview" />
					<div className="message-attachment-image-overlay">
						<EyeOutlined className="message-attachment-image-icon" />
					</div>
				</div>
			);
		}

		return (
			<div key={attachment.id} className="message-attachment" onClick={() => handleAttachmentClick(attachment)}>
				<div className="message-attachment-icon">{icon}</div>
				<div className="message-attachment-info">
					<Text className="message-attachment-name" ellipsis>
						{attachment.name}
					</Text>
					<Text type="secondary" className="message-attachment-size">
						{formatFileSize(attachment.size)}
					</Text>
				</div>
				<Button type="text" size="small" icon={<DownloadOutlined />} className="message-attachment-download" />
			</div>
		);
	};

	const renderMessage = (message: Message) => {
		const isOwn = isOwnMessage(message, currentUserId);
		const sender = message.sender;
		const senderName = sender ? `${sender.first_name} ${sender.last_name}` : "Unknown";
		const senderAvatar = sender?.profile_picture || undefined;
		const isRead = message.read_by && message.read_by.length > 1;
		const hasAttachments = message.attachments && message.attachments.length > 0;
		const isUploading = message.metadata?.uploading === true;

		const messageTime = new Date(message.created_at).toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		});

		return (
			<div key={message.id} style={{ display: "flex", gap: "12px", alignItems: "flex-end", justifyContent: isOwn ? "flex-end" : "flex-start", marginBottom: "12px" }}>
				{!isOwn && (
					<Avatar
						size={40}
						icon={!senderAvatar ? <UserOutlined /> : undefined}
						src={senderAvatar}
						style={{ flexShrink: 0 }}
					/>
				)}

				<div style={{ display: "flex", flexDirection: "column", gap: "4px", maxWidth: "60%", alignItems: isOwn ? "flex-end" : "flex-start" }}>
					{!isOwn && (
						<Typography.Text type="secondary" style={{ fontSize: "11px" }}>
							{senderName}
						</Typography.Text>
					)}

					<div
						style={{
							backgroundColor: isOwn ? "var(--mg-primary)" : "var(--mg-bg-subtle)",
							border: isOwn ? "none" : "1px solid var(--mg-border)",
							borderRadius: "12px",
							padding: "8px 12px",
							opacity: isUploading ? 0.65 : 1,
						}}
					>
						<Space direction="vertical" style={{ width: "100%" }} size={4}>
							{message.content && message.content.trim() && (
								<Typography.Text
									style={{
										color: isOwn ? "var(--mg-text-on-primary)" : "var(--mg-text)",
										fontSize: "14px",
										lineHeight: "1.5",
										whiteSpace: "pre-wrap",
										wordBreak: "break-word",
									}}
								>
									{message.content}
								</Typography.Text>
							)}

							{hasAttachments && (
								<div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
									{message.attachments!.map((attachment, index) =>
										isUploading
											? renderUploadingAttachment(attachment, index)
											: renderAttachment(attachment)
									)}
								</div>
							)}

							<div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: isOwn ? "flex-end" : "flex-start", marginTop: "4px" }}>
								<Typography.Text
									type="secondary"
									style={{
										fontSize: "11px",
										color: isOwn ? "rgba(255, 255, 255, 0.65)" : "var(--mg-text-secondary)",
									}}
								>
									{messageTime}
								</Typography.Text>

								{isOwn && !isUploading && (
									<span style={{ display: "flex", alignItems: "center", fontSize: "12px", color: isOwn ? "rgba(255, 255, 255, 0.85)" : "var(--mg-primary)" }}>
										{isRead ? (
											<Tooltip title="Read">
												<DoubleRightOutlined style={{ color: isOwn ? "rgba(255, 255, 255, 0.85)" : "var(--mg-primary)" }} />
											</Tooltip>
										) : (
											<Tooltip title="Sent">
												<CheckOutlined style={{ color: isOwn ? "rgba(255, 255, 255, 0.65)" : "var(--mg-text-secondary)" }} />
											</Tooltip>
										)}
									</span>
								)}

								{isUploading && <Spin size="small" />}
							</div>
						</Space>
					</div>
				</div>
			</div>
		);
	};

	const renderUploadingAttachment = (attachment: any, index: number) => {
		const icon = getFileIcon(attachment.type);

		return (
			<div key={`uploading-${index}`} className="message-attachment message-attachment-uploading">
				<div className="message-attachment-icon">{icon}</div>
				<div className="message-attachment-info">
					<Text className="message-attachment-name" ellipsis>
						{attachment.name}
					</Text>
					<Text type="secondary" className="message-attachment-size">
						{formatFileSize(attachment.size)}
					</Text>
				</div>
				<Spin size="small" />
			</div>
		);
	};

	return (
		<div className="conversation-thread">
			<div className="conversation-thread-header">
				{onBack && <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} className="conversation-back-button" />}

				<div className="conversation-thread-title">
					<Text strong>{conversationTitle}</Text>
					{conversation.participants && (
						<Text type="secondary" className="text-xs">
							{activeParticipants.length} participant{activeParticipants.length !== 1 ? "s" : ""}
							{conversation.is_closed && " • Closed"}
						</Text>
					)}
				</div>

				<Space size={4}>
					<Popover title="Participants" content={participantsPopoverContent} trigger="click" open={participantsOpen} onOpenChange={setParticipantsOpen} placement="bottomRight">
						<Tooltip title="View participants">
							<Button type="text" icon={<TeamOutlined />} />
						</Tooltip>
					</Popover>

					{!isPatient && onAddParticipant && !conversation.is_closed && (
						<Tooltip title="Add practitioner">
							<Button type="text" icon={<UserAddOutlined />} onClick={onAddParticipant} className="conversation-add-participant-button" />
						</Tooltip>
					)}

					{canCloseConversation && (
						<Tooltip title="Close conversation">
							<Button type="text" danger icon={<CloseCircleOutlined />} onClick={handleCloseConversation} loading={isClosing} className="conversation-close-button" />
						</Tooltip>
					)}
				</Space>
			</div>

			{conversation.is_closed && (
				<Alert
					message="Conversation Closed"
					description={
						isPatient
							? "This conversation has been closed by a practitioner. To continue communicating, please start a new conversation."
							: "This conversation has been closed. The patient will need to submit a new request to continue communicating."
					}
					type="warning"
					showIcon
					style={{ margin: "16px", marginBottom: 0 }}
				/>
			)}

			<div className="conversation-thread-messages" ref={messagesContainerRef}>
				{isLoading ? (
					<div className="flex items-center justify-center h-full">
						<Spin size="large" />
					</div>
				) : messages.length === 0 ? (
					<div className="flex items-center justify-center h-full">
						<Empty description="No messages yet. Start the conversation!" />
					</div>
				) : (
					<>
						{Object.entries(groupedMessages).map(([date, dateMessages]) => (
							<div key={date} className="message-date-group">
								<div className="message-date-separator">
									<Text type="secondary" className="text-xs">
										{date}
									</Text>
								</div>

								{dateMessages.map(renderMessage)}
							</div>
						))}

						{typingUserNames && (
							<div className="typing-indicator-wrapper">
								<div className="typing-indicator">
									<Text type="secondary" className="text-xs">
										{typingUserNames}
									</Text>
									<div className="typing-dots">
										<span></span>
										<span></span>
										<span></span>
									</div>
								</div>
							</div>
						)}

						<div ref={messagesEndRef} />
					</>
				)}
			</div>

			<MessageInput conversationId={conversation.id} userId={currentUserId} onSendMessage={onSendMessage} disabled={isLoading || conversation.is_closed} />

			{previewImage && (
				<Image
					style={{ display: "none" }}
					src={previewImage}
					preview={{
						visible: !!previewImage,
						onVisibleChange: (visible) => {
							if (!visible) setPreviewImage(null);
						},
					}}
				/>
			)}
		</div>
	);
};

export default ConversationThread;
